import { z } from 'zod';
// Cards Via writes to explain what happened: one conversation's chain of evidence, or many conversations grouped by
// cause. Via chooses what to say; the tools check every turn, run, step and conversation it points at, and leave off
// any that don't exist, so a card never links somewhere wrong.
// A group lists a few examples, or every conversation when the user asks to list all of it. The tool checks the first
// few; the card loads the rest as it shows them.
const MAX_LISTED = 50;
const EXAMPLES = 5;
export const GROUP_CONVERSATIONS_CHECKED = 5;
const MAX_SUMMARY_CHARS = 140;
const conversationKindSchema = z.enum(['call', 'chat', 'email']);
const conversationRefSchema = z.object({ kind: conversationKindSchema, conversationId: z.string() });
const evidenceSchema = z.object({
    kind: z
        .enum(['said', 'agent', 'action', 'step', 'path', 'qa', 'feedback', 'analysis'])
        .describe("said: the customer's words; agent: what the agent said or did; action: an action it called; step: a workflow step's result or error; path: which way a workflow branch went; qa: a QA criterion's result; feedback: a reviewer's note; analysis: a field the analysis extracted"),
    text: z
        .string()
        .describe("One short line in plain words, quoting where it helps; say how a call ended as the user would, not a raw value like 'customer-ended-call'"),
    turn: z.number().int().optional().describe('The transcript turn it happened at'),
    workflowRunId: z.string().optional().describe('The run it happened in'),
    stepId: z.string().optional().describe('The step in that run, from get_workflow_run')
});
export const showDiagnosisToolSchema = {
    name: 'show_diagnosis',
    description: "Show what happened in one conversation as a card: the chain of evidence in the order it happened, from what the customer said to the outcome, each item linked to its turn or workflow step, then the cause and the fix. The card has a Fix this button that asks you to fix the cause. Use it every time you answer why something happened in one conversation, including when the cause is the agent's own choice rather than a failure (point at the line where it went wrong and the result or instruction it had), with 2–6 items, pointing only at turns, runs and steps you read. Afterwards, add one or two short sentences only for what the card can't hold, such as a follow-up the customer needs; when there is nothing, add nothing.",
    inputSchema: z.object({
        question: z
            .string()
            .describe('What the user asked, as a short heading, such as "Why did the call end early?"'),
        conversation: conversationRefSchema,
        evidence: z.array(evidenceSchema).min(1).max(8),
        cause: z.string().describe('The cause, in one or two sentences'),
        fix: z
            .string()
            .optional()
            .describe('The one fix you would make in Strada, in one sentence; when it takes several changes, name them together. Never a choice between alternatives. Leave it out when there is nothing to change, such as a customer who simply hung up; the Fix this button only shows with a fix')
    }),
    outputSchema: z.object({
        evidence: z.array(evidenceSchema).describe('The evidence as shown, with references that exist'),
        dropped: z.array(z.string()).describe("References that don't exist, left off the card")
    })
};
const MAX_MOMENTS = 5;
const momentSchema = z.object({
    turn: z.number().int().describe('The transcript turn, from get_conversation'),
    note: z.string().describe('What happens at this turn, in a few words')
});
export const showConversationToolSchema = {
    name: 'show_conversation',
    description: "Show one call, chat or email thread as a card, the way History shows it: its shape across the top (who spoke when on a call; when each side wrote in a chat or thread), then the moments you point to, each with a short note, with the turns between them folded for the user to open, and History a click away. Use it when the user wants to see a conversation or where something happened in it, such as the call where the customer asked for a refund. To explain why something went wrong, use show_diagnosis instead. Read the conversation with get_conversation first so the turns are right. The card shows the words and your notes, so don't retell or quote them: afterwards, reply in two or three sentences with the takeaway and what the card can't show, such as a follow-up the customer needs.",
    inputSchema: z.object({
        title: z
            .string()
            .describe('What the user should take from it, as a short heading, such as "Asked twice for the policy number"'),
        conversation: conversationRefSchema,
        moments: z
            .array(momentSchema)
            .max(MAX_MOMENTS)
            .describe(`Up to ${MAX_MOMENTS} turns worth reading, in order. Leave it empty to show the turns that stand out on their own: QA failures, feedback and failed actions`)
    }),
    outputSchema: z.object({
        moments: z.array(momentSchema).describe('The moments as shown, at turns that exist'),
        dropped: z.array(z.string()).describe("Turns that don't exist, left off the card")
    })
};
/** The moments at turns the conversation has; the rest are left off and reported. */
export const checkMoments = ({ moments }, turns) => ({
    moments: moments.filter(({ turn }) => turn >= 0 && turn < turns),
    dropped: moments.filter(({ turn }) => turn < 0 || turn >= turns).map(({ turn }) => `turn ${turn}`)
});
const listedConversationSchema = conversationRefSchema.extend({
    startedAt: z.string(),
    summary: z.string().optional()
});
const groupInputSchema = z.object({
    label: z.string().describe('The cause or outcome, in a few words'),
    detail: z.string().optional().describe('One sentence on what these conversations have in common'),
    count: z
        .number()
        .int()
        .min(1)
        .describe('How many conversations have this cause, including ones not listed'),
    conversations: z
        .array(conversationRefSchema)
        .max(MAX_LISTED)
        .describe(`Up to ${EXAMPLES} examples, most telling first, from ones you found or read; every one (up to ${MAX_LISTED}) when the user asks to list all of the group`)
});
export const showConversationGroupsToolSchema = {
    name: 'show_conversation_groups',
    description: "Show many conversations grouped as a card, by cause or by how they ended, such as why calls failed today or how this month's chats ended: each group with its label, how many conversations it covers, and a few examples to open; the card offers List all for the rest. When the user asks to list all of a group, show that group alone with every conversation in it. Group by cause only after reading enough of them to know the causes; biggest group first. Afterwards, keep your reply to anything the card doesn't say.",
    inputSchema: z.object({
        title: z.string().describe('What the groups explain, such as "Why lookups failed this week"'),
        period: z.string().optional().describe('The date range, as the user would say it'),
        groups: z.array(groupInputSchema).min(1).max(6)
    }),
    outputSchema: z.object({
        groups: z.array(groupInputSchema.extend({
            conversations: z
                .array(listedConversationSchema)
                .describe(`The first ${GROUP_CONVERSATIONS_CHECKED}, checked`),
            // Optional so cards saved before it still read.
            alsoListed: z
                .number()
                .optional()
                .describe('How many more the card lists after these, loading each as it shows')
        })),
        dropped: z.array(z.string()).describe("Conversations that don't exist, left off the card")
    })
};
/** The diagnosis with every reference checked: ones that don't exist are left off and reported. */
export const checkDiagnosis = async ({ evidence }, references) => {
    const dropped = [];
    const runIds = new Set(references.workflowRunIds);
    const checked = await Promise.all(evidence.map(async ({ turn, workflowRunId, stepId, ...item }) => {
        const turnExists = turn !== undefined && turn >= 0 && turn < references.turns;
        const runExists = workflowRunId !== undefined && runIds.has(workflowRunId);
        const steps = runExists ? await references.stepIds(workflowRunId) : undefined;
        const stepExists = stepId !== undefined && (steps?.includes(stepId) ?? false);
        if (turn !== undefined && !turnExists)
            dropped.push(`turn ${turn}`);
        if (workflowRunId !== undefined && !runExists)
            dropped.push(`run ${workflowRunId}`);
        if (stepId !== undefined && !stepExists)
            dropped.push(`step ${stepId}`);
        return {
            ...item,
            ...(turnExists && { turn }),
            ...(runExists && { workflowRunId }),
            ...(stepExists && { stepId })
        };
    }));
    return { evidence: checked, dropped };
};
const shorten = (text) => text.length > MAX_SUMMARY_CHARS ? `${text.slice(0, MAX_SUMMARY_CHARS)}…` : text;
/** The groups with each conversation looked up; ones that don't exist are left off and reported. */
export const checkConversationGroups = async ({ groups }, lookUp) => {
    const dropped = [];
    const checked = await Promise.all(groups.map(async ({ conversations, ...group }) => {
        const found = await Promise.all(conversations.slice(0, GROUP_CONVERSATIONS_CHECKED).map(async (ref) => {
            const conversation = await lookUp(ref);
            if (!conversation) {
                dropped.push(`${ref.kind} ${ref.conversationId}`);
                return [];
            }
            return [
                {
                    ...ref,
                    startedAt: conversation.startedAt,
                    ...(conversation.summary && { summary: shorten(conversation.summary) })
                }
            ];
        }));
        const alsoListed = conversations.length - GROUP_CONVERSATIONS_CHECKED;
        return { ...group, conversations: found.flat(), ...(alsoListed > 0 && { alsoListed }) };
    }));
    return { groups: checked, dropped };
};
//# sourceMappingURL=diagnosis.js.map